import { Icon, Image, Typography } from 'beek-design-system';
import { useTranslations } from 'next-intl';
import Const from 'src/config/Const';
import styled from 'styled-components';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: GraphQL imports are bugged but functional. Ignored for now to make CircleCI happy.
import { Affiliate } from 'src/api/landing/landing.graphql';

type BenefitSectionProps = {
  data: string[];
  hasDiscount?: boolean;
  price?: string;
  discountPrice?: string;
  discountPercentage?: string;
  urlImage?: string;
  interval?: number;
  intervalUnit?: string;
  affiliate?: Affiliate | null;
};

const defaultProps = {
  hasDiscount: false,
  price: '',
  discountPrice: '',
  discountPercentage: '',
  urlImage: '',
  interval: '',
  intervalUnit: '',
  affiliate: {},
};

type TextContainerProps = {
  marginBottom: string;
};

const BackgroundContainer = styled.section`
  background: linear-gradient(
    180deg,
    var(--cold-blue) 0%,
    var(--sky-blue) 50%,
    var(--beek-blue) 100%
  );
  border-radius: 0.75rem;
  display: grid;
  top: 0;
  margin: 5rem 1rem 4.483rem 1rem;
  grid-template-columns: 1fr;
  grid-template-areas:
    'title'
    'cover'
    'info';
  padding: 1.5rem 1rem 2rem 1rem;
  @media ${({ theme }): string => theme.devices.laptop} {
    max-height: 29rem;
    position: relative;
    margin: 12.5rem 0 0 0;
    padding: 5rem 0 7rem 0;
    grid-template-columns: 27.875rem 23.25rem;
    grid-template-areas:
      'title cover'
      'info cover';
    column-gap: 4.375rem;
    justify-content: center;
    border-radius: 0;
  }
`;

const TitleContainer = styled(Typography)`
  grid-area: title;
  width: 16rem;
  margin: 0 auto 1rem auto;
  text-align: center;
  @media ${({ theme }): string => theme.devices.mobileM} {
    width: 19.438rem;
  }
  @media ${({ theme }): string => theme.devices.laptop} {
    width: 28.125rem;
    margin: 0 0 1.5rem 2rem;
    text-align: left;
  }
`;

const InfoContainer = styled.section`
  grid-area: info;
  margin: 0 auto;
  @media ${({ theme }): string => theme.devices.laptop} {
    margin: 0;
  }
`;

const TextContainer = styled.div<TextContainerProps>`
  display: flex;
  margin-bottom: ${(props): string => props.marginBottom};
  @media ${({ theme }): string => theme.devices.laptop} {
    margin-bottom: 1rem;
  }
`;

const ImageContainer = styled.div`
  grid-area: cover;
  margin: 0 auto 1rem auto;
  @media ${({ theme }): string => theme.devices.laptop} {
    margin: 1rem 0 0 0;
    position: absolute;
  }
`;

const TextLine = styled(Typography)`
  text-decoration: line-through;
  color: var(--red);
`;

const DiscountContainer = styled.div`
  grid-area: title;
  width: 16rem;
  margin: 0 auto 1.5rem auto;
  text-align: center;
  @media ${({ theme }): string => theme.devices.mobileM} {
    width: 19.438rem;
  }
  @media ${({ theme }): string => theme.devices.laptop} {
    width: 28.125rem;
    margin: 0 0 2rem 2rem;
    text-align: left;
  }
`;

const TagContainer = styled.div`
  display: none;
  border-radius: 0.125rem;
  @media ${({ theme }): string => theme.devices.laptop} {
    display: block;
    margin-left: 19.2rem;
    margin-top: -26rem;
  }
`;

const TagTop = styled.div`
  background-color: var(--red);
  height: 1.544rem;
  width: 1.7rem;
  border-radius: 0.1rem;
  transform: translateY(2.14rem) translateX(-0.75rem) rotate(39deg);
  margin-left: 0.04rem;
`;
const TagBottom = styled.div`
  background-color: var(--red);
  display: flex;
  justify-content: flex-end;
  height: 2.281rem;
  width: 3.625rem;
  padding: 0.769rem 0.769rem 0.769rem;
  border-radius: 0.125rem;
  transform: rotate(-7deg);
`;
const TagValue = styled.span`
  display: flex;
  align-self: center;
  font-size: 1.148rem;
  font-weight: bold;
  color: var(--white);
`;

const TagContainerMobile = styled.div`
  display: flex;
  margin-right: 0.1rem;
  position: absolute;
  @media ${({ theme }): string => theme.devices.laptop} {
    display: none;
  }
`;

const TagTopMobile = styled.div`
  background-color: var(--red);
  height: 1.34rem;
  width: 1.281rem;
  border-radius: 0.1rem;
  transform: translateY(2.22rem) translateX(-0.74rem) rotate(44deg);
  margin-right: -1.4rem;
  margin-top: -2.94rem;
`;

const TagBottomMobile = styled.div`
  background-color: var(--red);
  padding: 0.25rem 0.5rem 0.25rem 0rem;
  width: 2.813rem;
  height: 1.75rem;
  border-radius: 0 0.125rem 0.125rem 0;
  margin-top: -1rem;
  margin-right: -1.5rem;
  transform: rotate(0deg);
`;

const TagValueMobile = styled.span`
  font-size: 0.875rem;
  font-weight: bold;
  color: var(--white);
`;

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const BenefitText = styled(Typography)`
  white-space: normal;
  @media ${({ theme }): string => theme.devices.laptop} {
    white-space: nowrap;
  }
`;

const BenefitSection = (props: BenefitSectionProps): JSX.Element => {
  const t = useTranslations('landing');
  const {
    data,
    hasDiscount,
    price,
    discountPrice,
    discountPercentage,
    interval,
    intervalUnit,
    affiliate,
  } = props;
  let months = '';
  if (intervalUnit)
    months =
      interval && interval > 1
        ? String(t(`units.${intervalUnit}s`))
        : String(t(`units.${intervalUnit}`));

  let month = '';
  if (intervalUnit) month = String(t(`units.${intervalUnit}`));

  let affiliateName = '';
  if (affiliate) affiliateName = affiliate.name;

  return (
    <BackgroundContainer>
      {hasDiscount ? (
        <Container data-testid="benefit-discount">
          <DiscountContainer>
            <Typography
              type="headline1"
              colorVariant="gray100"
              marginBottom="0.5rem"
            >
              {`${t('benefit.discountTitle')}`}
            </Typography>
            <Typography type="body1" colorVariant="gray100" display="inline">
              {`${t('benefit.discountDescription')}`}
            </Typography>
            <TextLine type="body1" colorVariant="red" display="inline">
              {price || ''}
            </TextLine>
            <Typography type="body1" colorVariant="gray100" display="inline">
              {` ${discountPrice}` || ''}
            </Typography>
            {affiliate?.affiliateType === 'bank' ? (
              <Typography type="body1" colorVariant="gray100" display="inline">
                {`${t(`benefit.copyAffiliate`, {
                  interval,
                  months,
                  affiliateName,
                })}`}
              </Typography>
            ) : (
              <Typography type="body1" colorVariant="gray100" display="inline">
                {`${t('benefit.discountDuration', {
                  interval,
                  months,
                  month,
                })}`}
              </Typography>
            )}
          </DiscountContainer>
          <TagContainerMobile>
            <TagTopMobile />
            <TagBottomMobile>
              <TagValueMobile>{discountPercentage}</TagValueMobile>
            </TagBottomMobile>
          </TagContainerMobile>
        </Container>
      ) : (
        <TitleContainer
          type="headline1"
          colorVariant="gray100"
          data-testid="benefit-title"
        >
          {`${t('benefit.title')}`}
        </TitleContainer>
      )}
      <InfoContainer>
        {data.map((benefit, i) => (
          <TextContainer
            marginBottom={i === data.length - 1 ? '0rem' : '0.5rem'}
            key={benefit}
          >
            <Icon
              name="check"
              colorVariant="gray100"
              marginTop="0.438rem"
              marginRight="0.821rem"
              width="0.867rem"
              height="0.644rem"
            />
            <BenefitText type="body1" colorVariant="gray100" display="inline">
              {benefit}
            </BenefitText>
          </TextContainer>
        ))}
      </InfoContainer>
      <ImageContainer>
        <Image
          url={`${Const.cdnUrl}/landing_page/mobile/audiolibro-hooked-nir-eyal-ryan-hoover-audifonos.webp`}
          laptopUrl={`${Const.cdnUrl}/landing_page/desktop/audiolibro-hooked-nir-eyal-ryan-hoover-audifonos.webp`}
          type="headphones"
          alt={`${t('benefit.alt')}`}
        />
        {hasDiscount && (
          <TagContainer>
            <TagTop />
            <TagBottom>
              <TagValue>{discountPercentage}</TagValue>
            </TagBottom>
          </TagContainer>
        )}
      </ImageContainer>
    </BackgroundContainer>
  );
};

BenefitSection.defaultProps = defaultProps;

export default BenefitSection;
